"use client"

import { useSession } from "next-auth/react"
import { useEffect, useRef } from "react"
import { SessionManager, useSessionManager } from "@/lib/session-manager"

export function SessionMonitor() {
  const { data: session, status } = useSession()
  const sessionManager = SessionManager.getInstance()
  const { handleSessionExpired } = useSessionManager()
  const lastActivityRef = useRef(Date.now())
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined)

  useEffect(() => {
    if (status !== 'authenticated' || !session?.user || !(session.user as { id: string }).id) return

    // Set up session monitoring
    const setupSessionMonitoring = () => {
      // Clear any existing interval
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current)
      }

      // Check session validity every 5 minutes
      sessionCheckIntervalRef.current = setInterval(async () => {
        try {
          // Make a lightweight API call to check session validity
          const response = await fetch('/api/account', {
            method: 'GET',
            cache: 'no-store'
          })

          if (response.status === 401) {
            // Session expired
            console.log('Session expired detected by monitor')
            await handleSessionExpired()
          }
        } catch (error) {
          console.error('Session check failed:', error)
        }
      }, 5 * 60 * 1000) // Check every 5 minutes
    }

    // Track user activity to reset session timer
    const updateActivity = () => {
      lastActivityRef.current = Date.now()
    }

    // Add event listeners for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true)
    })

    setupSessionMonitoring()

    // Cleanup function
    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current)
      }
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true)
      })
    }
  }, [session, status, sessionManager, handleSessionExpired])

  // Set up session expiration handler
  useEffect(() => {
    sessionManager.setOnSessionExpired(handleSessionExpired)
  }, [sessionManager, handleSessionExpired])

  // This component doesn't render anything
  return null
}
